import { PropertyType } from '~/client/graph/types.generated'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import type { ResourceSemanticIconKey } from '~/client/src/shared/resources/definitions/types'

import type { IResourceLinkConfiguration } from './targets'

interface IManagedResourceLinkEnd {
  readonly resourceTypeId: string
  readonly label: string
  readonly pickerTitle: string
  readonly emptyMessage: string
  readonly semanticIconKey: ResourceSemanticIconKey
}

/**
 * A system-managed resource link and the presentation metadata for both ends.
 */
export interface IManagedResourceLinkDefinition {
  readonly kindId: string
  readonly propertyName: string
  readonly source: IManagedResourceLinkEnd
  readonly target: IManagedResourceLinkEnd
}

/**
 * Canonical frontend definition of the equipment-to-StruxTag managed link.
 */
export const EQUIPMENT_SENSOR_RESOURCE_LINK = {
  kindId: 'equipment_sensor',
  propertyName: 'linked_sensor',
  source: {
    resourceTypeId: ResourceTypeId.OFFLOADING_EQUIPMENT,
    label: 'Equipment',
    pickerTitle: 'Link Equipment',
    emptyMessage: 'No available Equipment',
    semanticIconKey: 'equipment',
  },
  target: {
    resourceTypeId: ResourceTypeId.SENSORS,
    label: 'StruxTag',
    pickerTitle: 'Link StruxTag',
    emptyMessage: 'No available StruxTags',
    semanticIconKey: 'sensor',
  },
} as const satisfies IManagedResourceLinkDefinition

const MANAGED_RESOURCE_LINKS = [EQUIPMENT_SENSOR_RESOURCE_LINK] as const

/**
 * Link-kind identifiers supported by the frontend managed-link runtime.
 */
export type ManagedResourceLinkKindId =
  (typeof MANAGED_RESOURCE_LINKS)[number]['kindId']

interface IManagedResourceLinkProperty {
  readonly resourceTypeId?: string | null
  readonly name?: string | null
  readonly propertyType?: PropertyType | string | null
  readonly configuration?: IResourceLinkConfiguration | null
}

/**
 * Finds a supported managed link by its source-side resource property.
 */
export const getManagedResourceLinkByProperty = ({
  resourceTypeId,
  name,
  propertyType,
  configuration,
}: IManagedResourceLinkProperty):
  | IManagedResourceLinkDefinition
  | undefined => {
  if (propertyType !== PropertyType.ResourceLink) return undefined

  const targetResourceType = configuration?.resourceLink?.targetResourceType

  return MANAGED_RESOURCE_LINKS.find(
    definition =>
      definition.source.resourceTypeId === resourceTypeId &&
      definition.propertyName === name &&
      definition.target.resourceTypeId === targetResourceType,
  )
}
